import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  TextField,
  Button,
  InputAdornment,
  Grid,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StatusChip from "../StatusChip";
import EditIcon from "@mui/icons-material/Edit";
import LeadRating from "../LeadRating";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import WorkIcon from "@mui/icons-material/Work";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CommentIcon from "@mui/icons-material/Comment";
import SaveIcon from "@mui/icons-material/Save";
import LeadCommunication from "./LeadCommunication";
import AddCommunication from "./AddCommunication";

const LeadDetailsDrawer = ({
  lead,
  onClose,
  onStatusChange,
  onLeadUpdate,
  onAddCommunication,
}) => {
  const [leadEdit, setLeadEdit] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [addCommunication, setAddCommunication] = useState(false);
  useEffect(() => {
    if (lead) {
      if (lead.id === -1) {
        setEditMode(true);
        setLeadEdit({
          leadId: -1,
          leadName: "",
          leadPotential: 1,
          leadPhone: "",
          leadEmail: "",
          leadCompany: "",
          leadStatus: "New",
          leadInitialComments: "",
          leadLandingPage: "",
        });
      } else {
        setLeadEdit({
          leadId: lead.id,
          leadName: lead.name || "",
          leadPotential: lead.strength || 0,
          leadPhone: lead.phone || "",
          leadEmail: lead.email || "",
          leadCompany: lead.company || "",
          leadInitialComments: lead.comments || "",
          leadLandingPage: lead.landingPage || "",
        });
      }
    }
  }, [lead]);
  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setLeadEdit((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveCommunication = (newComment) => {
    setAddCommunication(false);
    onAddCommunication(newComment);
  };

  const handleAddCommunication = () => {
    setAddCommunication(true);
  };

  if (!lead) return null;

  return (
    <Drawer
      anchor="left"
      open={Boolean(lead)}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "70%",
            padding: 3,
            overflowX: "hidden",
            mt: "90px",
            height: "calc(100% - 90px)",
            background: "#f2f2f2",
          },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography variant="h6">Lead Details</Typography>
          <Typography color="text.secondary" fontSize={14}>
            Lead Id: {lead.id}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Grid container spacing={4}>
        <Grid size={4}>
          <Box my={4}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{
                mb: 4,
                "&.Mui-disabled": {
                  color: "grey.500", // ← Use theme palette
                  borderColor: "grey.300", // For outlined buttons
                  backgroundColor: "grey.100", // Optional
                },
              }}
              onClick={() => setEditMode(true)}
              disabled={editMode}
            >
              Edit Record
            </Button>
            {/* Lead Info */}
            <Box mb={4}>
              <TextField
                defaultValue={lead.name}
                variant="outlined"
                sx={{
                  minWidth: "300px",
                  backgroundColor: editMode ? "#fff" : "",
                }}
                label={"Lead Name"}
                disabled={!editMode}
                onChange={handleChange("leadName")}
              />

              <Box my={2}>
                {/* <Chip label={lead.status} color={getStatusColor(lead.status)} /> */}
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  Lead Status
                </Typography>
                <StatusChip
                  status={lead.status}
                  onChange={(newStatus) => onStatusChange(lead.id, newStatus)}
                />
              </Box>
              <Box my={2}>
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  Lead Potential
                </Typography>
                <LeadRating
                  rating={
                    leadEdit && leadEdit.leadPotential
                      ? leadEdit.leadPotential
                      : lead.strength
                  }
                  readOnly={!editMode}
                  onChange={(value) =>
                    setLeadEdit((prev) => ({ ...prev, leadPotential: value }))
                  }
                />
              </Box>
              <Box my={2}>
                <TextField
                  defaultValue={lead.phone}
                  variant="outlined"
                  sx={{
                    minWidth: "300px",
                    backgroundColor: editMode ? "#fff" : "",
                  }}
                  label={"Lead Phone/Mobile"}
                  disabled={!editMode}
                  onChange={handleChange("leadPhone")}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneForwardedIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
              <Box my={4}>
                <TextField
                  defaultValue={lead.email}
                  variant="outlined"
                  sx={{
                    minWidth: "300px",
                    backgroundColor: editMode ? "#fff" : "",
                  }}
                  disabled={!editMode}
                  label={"Lead Email"}
                  onChange={handleChange("leadEmail")}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AlternateEmailIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
              <Box my={4}>
                <TextField
                  defaultValue={lead.company}
                  variant="outlined"
                  label={"Company Name"}
                  sx={{
                    minWidth: "300px",
                    backgroundColor: editMode ? "#fff" : "",
                  }}
                  onChange={handleChange("leadCompany")}
                  disabled={!editMode}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
              <Box my={4}>
                <TextField
                  defaultValue={lead.landing_page}
                  variant="outlined"
                  label={"Landing Page"}
                  sx={{
                    minWidth: "300px",
                    backgroundColor: editMode ? "#fff" : "",
                  }}
                  onChange={handleChange("landingPage")}
                  disabled={!editMode}
                />
              </Box>

              <Box my={4}>
                <TextField
                  defaultValue={lead.comments}
                  variant="outlined"
                  fullWidth
                  multiline
                  label={"Initial Comments"}
                  onChange={handleChange("leadInitialComments")}
                  disabled={!editMode}
                  sx={{ backgroundColor: editMode ? "#fff" : "" }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CommentIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Box>
            {editMode && (
              <Box display={"flex"} justifyContent={"flex-start"} gap={2}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    setEditMode(false);
                    onLeadUpdate(leadEdit);
                  }}
                >
                  Save
                </Button>
                <Button onClick={() => setEditMode(false)}>Cancel</Button>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid size={1}>
          <Divider orientation="vertical" />
        </Grid>

        <Grid size={7}>
          {/* Communications */}
          <Box mt={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Communication Notes
            </Typography>

            <Box mb={4}>
              <IconButton
                size="small"
                color="secondary"
                onClick={handleAddCommunication}
              >
                <AddBoxIcon />
              </IconButton>
            </Box>
            {addCommunication && (
              <AddCommunication
                onSave={handleSaveCommunication}
                onClose={() => setAddCommunication(false)}
              />
            )}
            <Stack spacing={2}>
              {lead.communications && lead.communications.length > 0 ? (
                lead.communications.map((note) => (
                  <LeadCommunication key={note.id} communication={note} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No communication notes yet.
                </Typography>
              )}
              <LeadCommunication />
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default LeadDetailsDrawer;
